import React from 'react';
import PropTypes from 'prop-types';
import {
  Tag,
  Typography,
  Table,
} from 'antd';
import {
  stripProductSkuShortLabel,
  sortSkusByContainerType,
  getSkuContainerGroupLabel,
} from '../../../../../util/sku-formatter';

const { Text, Title } = Typography;

/** Display RRP in the summary table with a dollar prefix; leaves barcodes unchanged. */
function formatRrpDisplay(value) {
  if (value == null) return null;
  const s = String(value).trim();
  if (s === '') return null;
  if (s.startsWith('$')) return s;
  return `$${s}`;
}

/** Split ordered SKUs into { label, skus } groups (same order as SKUs tab). */
function groupSkusByContainer(orderedSkus) {
  const groups = [];
  orderedSkus.forEach((sku) => {
    const label = getSkuContainerGroupLabel(sku);
    const last = groups[groups.length - 1];
    if (!last || last.label !== label) {
      groups.push({ label, skus: [sku] });
    } else {
      last.skus.push(sku);
    }
  });
  return groups;
}

const SkuSummary = ({ skus, productName }) => {
  if (!skus || skus.length === 0) return null;

  const orderedSkus = sortSkusByContainerType(skus);
  const groups = groupSkusByContainer(orderedSkus);

  const stripProductName = (name) => stripProductSkuShortLabel(name, productName || '');

  const renderTagList = (items) => {
    const populated = items.filter(([, value]) => value);
    if (populated.length === 0) {
      return <Text type="secondary" italic>—</Text>;
    }
    return (
      <div className="sku-tag-list">
        {populated.map(([label, value]) => (
          <div key={label} className="sku-tag-item">
            <Text type="secondary" className="sku-tag-label">{label}</Text>
            <Text copyable className="sku-tag-value">{value}</Text>
          </div>
        ))}
      </div>
    );
  };

  const columns = [
    {
      title: 'SKU',
      dataIndex: 'name',
      key: 'name',
      width: 240,
      render: (name, record) => (
        <div>
          <Text strong>{stripProductName(name) || 'Unnamed SKU'}</Text>
          {record.isVenueSku && (
            <Tag color="purple" style={{ marginLeft: 8 }}>Venue</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'RRP',
      key: 'rrp',
      render: (_, record) => renderTagList([
        ['Unit', formatRrpDisplay(record.rrpUnit)],
        ['Pack', formatRrpDisplay(record.rrpPack)],
        ['Case', formatRrpDisplay(record.rrpCase)],
      ]),
    },
    {
      title: 'Barcodes',
      key: 'barcodes',
      render: (_, record) => renderTagList([
        ['Unit', record.bnUnit],
        ['Pack', record.bnPack],
        ['Case', record.bnCase],
      ]),
    },
  ];

  return (
    <div className="about-tab-sku-summary-section">
      <Title level={5} className="about-tab-section-title">
        SKU Info
      </Title>
      {groups.map(({ label, skus: groupSkus }) => (
        <div key={label} className="sku-summary-group">
          <div className="product-editor-sku-tab-group">{label}</div>
          <Table
            className="sku-summary-table"
            dataSource={groupSkus}
            columns={columns}
            rowKey="key"
            pagination={false}
            size="small"
          />
        </div>
      ))}
    </div>
  );
};

SkuSummary.propTypes = {
  productName: PropTypes.string,
  skus: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.number,
    name: PropTypes.string,
    isLegacy: PropTypes.bool,
    isVenueSku: PropTypes.bool,
    rrpUnit: PropTypes.string,
    rrpPack: PropTypes.string,
    rrpCase: PropTypes.string,
    bnUnit: PropTypes.string,
    bnPack: PropTypes.string,
    bnCase: PropTypes.string,
  })),
};

SkuSummary.defaultProps = {
  productName: null,
  skus: [],
};

export default SkuSummary;
